import React, { useEffect, useMemo, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import Stack from "@mui/joy/Stack";
import Input from "@mui/joy/Input";
import Autocomplete from "@mui/joy/Autocomplete";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import { Button, IconButton, CircularProgress } from "@mui/joy";
import DeleteIcon from "@mui/icons-material/Delete";
import { useBKGVCSearch } from "../../../modules/bkg/services/api";
import {
  validateEntityName,
  reindexErrors,
} from "../../../modules/bkg/utils/validateEntityName";

interface AddVCModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: Record<string, any>) => Promise<any>;
  vcFirmOptions?: Array<{ id: string; name: string }>;
  workspaceId: string;
}

const AddVCModal: React.FC<AddVCModalProps> = ({
  open,
  onClose,
  onAdd,
  vcFirmOptions = [],
  workspaceId,
}) => {
  const [vcFirmName, setVcFirmName] = useState<string>("");
  const [entries, setEntries] = useState<
    Array<{ person: string; role: string }>
  >([{ person: "", role: "" }]);
  const [loading, setLoading] = useState<boolean>(false);
  const [firmError, setFirmError] = useState<string | null>(null);
  const [personErrors, setPersonErrors] = useState<Record<number, string>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const bkgVCSearch = useBKGVCSearch();

  useEffect(() => {
    if (!open) {
      setVcFirmName("");
      setEntries([{ person: "", role: "" }]);
      setLoading(false);
      setFirmError(null);
      setPersonErrors({});
      setSubmitAttempted(false);
    }
  }, [open]);

  const firmLookup = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    vcFirmOptions.forEach((option) => {
      map.set(option.name.trim().toLowerCase(), option);
    });
    return map;
  }, [vcFirmOptions]);

  const firmNames = useMemo(
    () => vcFirmOptions.map((option) => option.name).sort(),
    [vcFirmOptions],
  );

  // Note: We no longer generate fallback document IDs on the frontend.
  // The backend MUST return the authoritative document_id, and we abort
  // contact creation if it doesn't. This prevents orphaned vc_person records.

  const extractDocumentIdFromResponse = (response: any): string | undefined => {
    if (!response) return undefined;

    if (typeof response === "string") {
      return response;
    }

    if (typeof response.document_id === "string") {
      return response.document_id;
    }

    if (typeof response._id === "string") {
      return response._id;
    }

    if (typeof response.id === "string") {
      return response.id;
    }

    if (
      Array.isArray(response.document_ids) &&
      typeof response.document_ids[0] === "string"
    ) {
      return response.document_ids[0];
    }

    if (Array.isArray(response.documents)) {
      const docWithId = response.documents.find(
        (doc: any) =>
          typeof doc?.document_id === "string" || typeof doc?._id === "string",
      );
      if (docWithId) {
        return docWithId.document_id ?? docWithId._id;
      }
    }

    if (Array.isArray(response.results)) {
      const resultWithId = response.results.find(
        (result: any) =>
          typeof result?.document_id === "string" ||
          typeof result?._id === "string",
      );
      if (resultWithId) {
        return resultWithId.document_id ?? resultWithId._id;
      }
    }

    if (response.document) {
      const nested = extractDocumentIdFromResponse(response.document);
      if (nested) return nested;
    }

    if (response.result) {
      const nested = extractDocumentIdFromResponse(response.result);
      if (nested) return nested;
    }

    if (response.data) {
      const nested = extractDocumentIdFromResponse(response.data);
      if (nested) return nested;
    }

    return undefined;
  };

  const extractFirmFromResults = (
    results: Array<Record<string, any>>,
    targetName: string,
  ): { id?: string; name?: string } => {
    const normalizedTarget = targetName.trim().toLowerCase();

    for (const item of results) {
      const id =
        (typeof item?._id === "string" && item._id) ||
        (typeof item?.document_id === "string" && item.document_id) ||
        (typeof item?.value?.document_id === "string" &&
          item.value.document_id);

      const rawName =
        (typeof item?.value?.name === "string" && item.value.name) ||
        (typeof item?.value?.name?.value === "string" &&
          item.value.name.value) ||
        (typeof item?.name === "string" && item.name) ||
        (typeof item?.name?.value === "string" && item.name.value);

      if (rawName && rawName.trim().toLowerCase() === normalizedTarget) {
        return { id, name: rawName };
      }
    }

    const fallback = results.find(
      (item) =>
        typeof (item?._id ?? item?.document_id ?? item?.value?.document_id) ===
        "string",
    );

    if (fallback) {
      return {
        id:
          fallback._id ??
          fallback.document_id ??
          fallback?.value?.document_id ??
          undefined,
        name:
          (typeof fallback?.value?.name === "string" && fallback.value.name) ||
          (typeof fallback?.value?.name?.value === "string" &&
            fallback.value.name.value) ||
          (typeof fallback?.name === "string" && fallback.name) ||
          (typeof fallback?.name?.value === "string" && fallback.name.value) ||
          undefined,
      };
    }

    return { id: undefined, name: undefined };
  };

  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    index: number,
    field: "person" | "role",
  ) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text");
    const lines = paste.split("\n").filter((line) => line.trim());

    if (lines.length <= 1) {
      handleInputChange(index, field, paste);
      return;
    }

    const newEntries = [...entries];
    lines.forEach((line, i) => {
      const targetIndex = index + i;
      if (targetIndex >= newEntries.length) {
        newEntries.push({ person: "", role: "" });
      }
      newEntries[targetIndex] = {
        ...newEntries[targetIndex],
        [field]: line.trim(),
      };
    });

    setEntries(newEntries);
  };

  const handleInputChange = (
    index: number,
    field: "person" | "role",
    value: string,
  ) => {
    if (submitAttempted && field === "person") {
      const error = value.trim() ? validateEntityName(value, "Person name") : null;
      setPersonErrors((prev) => {
        const next = { ...prev };
        if (error) next[index] = error;
        else delete next[index];
        return next;
      });
    }

    setEntries((prev) => {
      const newEntries = [...prev];
      newEntries[index] = { ...newEntries[index], [field]: value };

      if (index === newEntries.length - 1 && value.trim() !== "") {
        newEntries.push({ person: "", role: "" });
      }

      return newEntries;
    });
  };

  const handleDelete = (index: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== index));
    setPersonErrors((prev) => reindexErrors(prev, index));
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    const firmNameError = validateEntityName(vcFirmName, "VC firm name");
    if (firmNameError) {
      setFirmError(firmNameError);
      return;
    }

    const newPersonErrors: Record<number, string> = {};
    entries.forEach((entry, idx) => {
      if (entry.person.trim()) {
        const err = validateEntityName(entry.person, "Person name");
        if (err) newPersonErrors[idx] = err;
      }
    });
    if (Object.keys(newPersonErrors).length > 0) {
      setPersonErrors(newPersonErrors);
      return;
    }

    setLoading(true);
    let hasErrors = false;

    const trimmedFirmName = vcFirmName.trim();
    const firmKey = trimmedFirmName.toLowerCase();
    let existingFirm = firmLookup.get(firmKey);

    // If not in the pre-loaded 200 options, search by name to avoid duplicates
    if (!existingFirm) {
      try {
        const searchResults = await bkgVCSearch(workspaceId, {
          filters: {
            name: { value: trimmedFirmName, type: "exact" },
          },
          limit: 1,
        });

        if (searchResults.results && searchResults.results.length > 0) {
          const resolvedFirm = extractFirmFromResults(
            searchResults.results,
            trimmedFirmName,
          );
          if (resolvedFirm.id) {
            existingFirm = {
              id: resolvedFirm.id,
              name: resolvedFirm.name ?? trimmedFirmName,
            };
            alert(
              `Found existing firm "${existingFirm.name ?? trimmedFirmName}". Adding contacts to this firm.`,
            );
          }
        }
      } catch (error) {
        console.error("Failed to search for existing firm:", error);
        // Continue anyway - better to create duplicate than fail completely
      }
    }

    let firmDocumentId = existingFirm?.id;
    let resolvedFirmName = existingFirm?.name ?? trimmedFirmName;

    if (!firmDocumentId) {
      const vcFirmData = {
        method: "manual",
        data: {
          index_name: "venture_capital",
          source_id: "manual",
          source_date: new Date().toISOString(),
          document: {
            name: trimmedFirmName,
          },
        },
      };

      try {
        const response = await onAdd(vcFirmData);
        // Backend returns document_id synchronously for VC firms (no race condition)
        firmDocumentId = extractDocumentIdFromResponse(response);

        if (!firmDocumentId) {
          // CRITICAL: Backend should ALWAYS return document_id for VC firms
          // If we don't have it, we MUST NOT proceed with contact creation
          // as we'd create orphaned vc_person records pointing to a guessed ID

          console.error(
            `Backend failed to return document_id for VC firm "${trimmedFirmName}". ` +
              `This indicates a backend processing error. Aborting contact creation.`,
          );

          alert(
            `Failed to create VC firm "${trimmedFirmName}". ` +
              `The backend did not return a valid firm identifier. ` +
              `Please try again or contact support if the issue persists.`,
          );

          setLoading(false);
          return; // ABORT - do not create contacts without authoritative firm ID
        }
      } catch (error) {
        console.error(`Failed to add VC firm: ${vcFirmName}`, error);
        alert(`Failed to add VC firm "${vcFirmName}". Please try again.`);
        hasErrors = true;
        setLoading(false);
        return; // Don't proceed if firm creation failed
      }
    }

    if (!firmDocumentId) {
      alert(
        `Could not resolve an identifier for "${trimmedFirmName}". Contacts were not added.`,
      );
      setLoading(false);
      return;
    }

    const filteredEntries = entries.filter(
      (e) => e.person.trim() || e.role.trim(),
    );
    let successCount = 0;

    for (const { person, role } of filteredEntries) {
      if (person.trim()) {
        const trimmedPerson = person.trim();
        const trimmedRole = role.trim();
        const personData = {
          method: "manual",
          data: {
            index_name: "vc_person",
            document: {
              name: trimmedPerson,
              role: trimmedRole || "Partner",
              vc_firm: {
                reference: firmDocumentId,
                name: resolvedFirmName,
              },
            },
          },
        };

        try {
          await onAdd(personData);
          successCount++;
        } catch (error) {
          console.error(`Failed to add VC person: ${person}`, error);
          alert(
            `Failed to add contact "${person}". Some contacts may not have been added.`,
          );
          hasErrors = true;
        }
      }
    }

    setLoading(false);

    if (!hasErrors) {
      onClose(); // Only close if no errors occurred
    } else if (successCount > 0) {
      // Partial success - inform user and close
      alert(
        `Added ${successCount} of ${filteredEntries.length} contacts. Please review the data.`,
      );
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        className="modal"
        sx={{ maxHeight: "80vh", overflowY: "auto" }}
        data-testid="add-vc-modal"
      >
        <DialogTitle>Add VC Firm & Contacts</DialogTitle>
        <Stack spacing={2}>
          <FormControl error={!!firmError}>
            <Autocomplete
              freeSolo
              placeholder="VC firm name"
              options={firmNames}
              value={vcFirmName || null}
              inputValue={vcFirmName}
              color={firmError ? "danger" : "neutral"}
              onInputChange={(_, newInputValue) => {
                setVcFirmName(newInputValue ?? "");
                if (submitAttempted) {
                  setFirmError(
                    validateEntityName(newInputValue ?? "", "VC firm name"),
                  );
                }
              }}
              onChange={(_, newValue) => {
                const val =
                  typeof newValue === "string"
                    ? newValue
                    : Array.isArray(newValue)
                      ? (newValue[0] ?? "")
                      : (newValue ?? "");
                setVcFirmName(val);
                if (submitAttempted) {
                  setFirmError(validateEntityName(val, "VC firm name"));
                }
              }}
              sx={{ width: "100%" }}
            />
            {firmError && <FormHelperText>{firmError}</FormHelperText>}
          </FormControl>
          {entries.map((entry, idx) => (
            <FormControl key={idx} error={!!personErrors[idx]}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Input
                  placeholder="Person name"
                  value={entry.person}
                  color={personErrors[idx] ? "danger" : "neutral"}
                  onChange={(e) =>
                    handleInputChange(idx, "person", e.target.value)
                  }
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                    handlePaste(e, idx, "person")
                  }
                  sx={{ flexGrow: 1 }}
                />
                <Input
                  placeholder="Role (e.g., Partner, Principal)"
                  value={entry.role}
                  onChange={(e) =>
                    handleInputChange(idx, "role", e.target.value)
                  }
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                    handlePaste(e, idx, "role")
                  }
                  sx={{ flexGrow: 1 }}
                />
                {(entry.person.trim() || entry.role.trim()) && (
                  <IconButton
                    size="sm"
                    color="danger"
                    onClick={() => handleDelete(idx)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
              {personErrors[idx] && (
                <FormHelperText>{personErrors[idx]}</FormHelperText>
              )}
            </FormControl>
          ))}
          {loading ? (
            <CircularProgress />
          ) : (
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="solid" color="neutral" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="solid"
                color="primary"
                onClick={handleSubmit}
                disabled={!vcFirmName.trim()}
              >
                Submit
              </Button>
            </Stack>
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default AddVCModal;
